import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router"
import {
  BarChart3,
  CalendarDays,
  ClipboardList,
  Coins,
  FileText,
  Gamepad2,
  Gift,
  Hash,
  LayoutDashboard,
  MapPin,
  Megaphone,
  MonitorUp,
  Settings,
  Users,
} from "lucide-react"
import { ConsoleSidebar, type ConsoleNavGroup, type ConsoleNavItem } from "@/components/ConsoleSidebar"
import { RoleGate } from "@/lib/role-gate"
import { useWildGuessSoloEnabled } from "@/lib/analytics/posthog"

export const Route = createFileRoute("/_authed/admin")({
  component: AdminShell,
})

interface AdminNavItem extends ConsoleNavItem {
  feature?: "solo"
}

interface AdminNavGroup {
  label: string
  items: AdminNavItem[]
}

const adminNavGroups: AdminNavGroup[] = [
  {
    label: "Overview",
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
      { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    ],
  },
  {
    label: "Live Ops",
    items: [
      { to: "/admin/events", label: "Events", icon: CalendarDays },
      { to: "/admin/games", label: "Games", icon: Gamepad2 },
      { to: "/admin/venues", label: "Venues", icon: MapPin },
      { to: "/admin/magic-numbers", label: "Magic Numbers", icon: Hash },
    ],
  },
  {
    label: "Audience",
    items: [
      { to: "/admin/users", label: "Users", icon: Users },
      { to: "/admin/rewards", label: "Rewards", icon: Gift },
      { to: "/admin/economy", label: "Economy", icon: Coins },
      { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
    ],
  },
  {
    label: "Solo",
    items: [{ to: "/admin/content", label: "Content", icon: FileText, feature: "solo" }],
  },
  {
    label: "System",
    items: [
      { to: "/admin/audit", label: "Audit", icon: ClipboardList },
      { to: "/admin/settings", label: "Settings", icon: Settings },
    ],
  },
]

const hostUtilityItem: ConsoleNavItem = {
  to: "/host",
  label: "Host",
  icon: MonitorUp,
  exact: true,
}

function AdminShell() {
  return (
    <RoleGate allowedRoles={["admin"]}>
      <AdminShellContent />
    </RoleGate>
  )
}

function AdminShellContent() {
  const matches = useMatches()
  const currentPath = matches[matches.length - 1]?.pathname ?? ""
  const soloEnabled = useWildGuessSoloEnabled()
  const visibleGroups: ConsoleNavGroup[] = adminNavGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.feature !== "solo" || soloEnabled),
    }))
    .filter((group) => group.items.length > 0)

  return (
    <ConsoleSidebar
      eyebrow="Admin console"
      title="Operations"
      currentPath={currentPath}
      navLabel="Admin sections"
      groups={visibleGroups}
      utilityItems={[hostUtilityItem]}
    >
      <Outlet />
    </ConsoleSidebar>
  )
}
